import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoFocus", "disabled", "error", "inputComponent", "cssStyles", "inputRef", "inputValue", "isDisplayOptimized", "onInputValueChange", "onOpenChange", "onPaste", "placeholder", "readOnly", "menuHeight", "menuStyles", "searchable", "selectComponent", "selectRef", "tabIndex", "value"];
import { Component } from 'react';
import { Controllable } from '../Controllable';
import { callIfPossible } from '../../utils/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { KeyboardModeContext } from '../../../components/AppProvider/KeyboardModeProvider';
import { HiddenDiv, StyledDropdownResults } from './Select.styles';
import { SearchInput } from '../SearchInput';
import unescapedText from 'I18n/utils/unescapedText';
import { getAriaAndDataProps } from '../../utils/Props';
import { withTokenProps } from '../../../utils/withTokenProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class SelectWithInputAnchorCore extends Component {
  constructor(...args) {
    super(...args);
    this.focus = () => {
      if (this._inputRef && typeof this._inputRef.focus === 'function') {
        this._inputRef.focus();
      }
    };
    this.selectRefCallback = ref => {
      const {
        selectRef
      } = this.props;
      this._selectRef = ref;
      if (selectRef) {
        selectRef(ref);
      }
    };
    this.handleInputKeyDown = evt => {
      const {
        readOnly
      } = this.props;
      const internalSelect = this.getInternalSelect();
      if (internalSelect && !readOnly) {
        internalSelect.handleKeyDown(evt);
      }
    };
    this.handleInputChange = evt => {
      const {
        onInputValueChange
      } = this.props;
      onInputValueChange === null || onInputValueChange === void 0 || onInputValueChange(evt);
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        internalSelect.handleInputChange(evt);
      }
    };
    this.handleInputFocus = () => {
      const {
        onOpenChange,
        readOnly
      } = this.props;
      const internalSelect = this.getInternalSelect();
      if (readOnly) return;
      if (internalSelect) {
        internalSelect.setState({
          isOpen: true
        });
      }
      callIfPossible(onOpenChange, syntheticEvent(true));
    };
    this.handleInputBlur = isKeyboardMode => {
      const {
        onOpenChange
      } = this.props;
      if (!isKeyboardMode) return;
      callIfPossible(onOpenChange, syntheticEvent(false));
    };
  }
  getInternalSelect() {
    // Return our react-select component instance, or null
    if (!this._selectRef) {
      return null;
    }
    if (!('select' in this._selectRef) || !this._selectRef.select) {
      return this._selectRef;
    }
    return this._selectRef.select;
  }
  getInternalAsyncSelect() {
    // Return our react-select Async instance, or null
    if (!this._selectRef || !('select' in this._selectRef) || !this._selectRef.select) {
      return null;
    }
    return this._selectRef;
  }
  render() {
    const _this$props = this.props,
      {
        autoFocus,
        disabled,
        error,
        inputComponent = SearchInput,
        cssStyles,
        inputRef,
        inputValue,
        isDisplayOptimized,
        onPaste,
        placeholder = unescapedText('salesUI.UISelect.placeholder'),
        readOnly,
        menuHeight,
        menuStyles,
        searchable = true,
        selectComponent,
        tabIndex,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const InputComponent = inputComponent;
    const SelectComponent = selectComponent;
    const {
      isKeyboardMode
    } = this.context;
    const ariaAndDataProps = getAriaAndDataProps(rest);
    return /*#__PURE__*/_jsxs(StyledDropdownResults, {
      $anchorType: "input",
      $cssStyles: cssStyles,
      $searchable: searchable,
      $menuHeight: menuHeight,
      $disabled: disabled,
      $menuStyles: menuStyles,
      children: [/*#__PURE__*/_jsx(InputComponent, Object.assign({}, ariaAndDataProps, {
        autoFocus: autoFocus,
        disabled: disabled,
        "aria-invalid": error,
        inputRef: inputRef,
        isDisplayOptimized: isDisplayOptimized,
        onBlur: () => this.handleInputBlur(isKeyboardMode),
        onChange: this.handleInputChange,
        onFocus: this.handleInputFocus,
        onKeyDown: this.handleInputKeyDown,
        onPaste: onPaste,
        placeholder: placeholder,
        readOnly: readOnly,
        ref: ref => {
          this._inputRef = ref;
        },
        required: ariaAndDataProps['aria-required'],
        tabIndex: tabIndex,
        value: inputValue
      })), /*#__PURE__*/_jsx(HiddenDiv, {
        children: /*#__PURE__*/_jsx(SelectComponent, Object.assign({}, rest, {
          autosize: false,
          disabled: disabled,
          ref: this.selectRefCallback,
          scrollMenuIntoView: false,
          value: value
        }))
      })]
    });
  }
}
SelectWithInputAnchorCore.contextType = KeyboardModeContext;
export const SelectWithInputAnchor = Controllable(withTokenProps(SelectWithInputAnchorCore), ['inputValue']);